import { AuditLog, AuditLogLink } from "../../../api/auditApi";

const ASSIGNMENT_ENTITY_TYPES = new Set([
  "VehicleAssignment",
  "TrailerAssignment",
  "DriverDispatcherAssignment",
]);

const ASSIGNMENT_ACTIONS = new Set([
  "VehicleAssigned",
  "VehicleUnassigned",
  "TrailerAssigned",
  "TrailerUnassigned",
  "DispatcherAssigned",
  "DispatcherUnassigned",
]);

export type AssignmentAssetType = "Vehicle" | "Trailer" | "Employee";

export interface AssignmentAssetInfo {
  entityType: AssignmentAssetType;
  entityId: number;
  assetLabel: string;
}

const parseMetadata = (metadata: string | null): Record<string, unknown> => {
  if (!metadata) {
    return {};
  }

  try {
    const parsed = JSON.parse(metadata);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const readNumber = (value: unknown) => {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

export const isAssignmentAudit = (auditLog: Pick<AuditLog, "entityType" | "action">) =>
  ASSIGNMENT_ENTITY_TYPES.has(auditLog.entityType) || ASSIGNMENT_ACTIONS.has(auditLog.action);

export const isAssignmentUnassignAction = (action: string) => action.toLowerCase().includes("unassign");

export const getAssignmentAssetInfo = (auditLog: AuditLog): AssignmentAssetInfo | null => {
  const metadata = parseMetadata(auditLog.metadata);
  const action = auditLog.action;

  if (action.startsWith("Vehicle")) {
    const entityId =
      auditLog.links.find((link) => link.entityType === "Vehicle")?.entityId ??
      readNumber(metadata.vehicleId);

    return entityId ? { entityType: "Vehicle", entityId, assetLabel: "truck" } : null;
  }

  if (action.startsWith("Trailer")) {
    const entityId =
      auditLog.links.find((link) => link.entityType === "Trailer")?.entityId ??
      readNumber(metadata.trailerId);

    return entityId ? { entityType: "Trailer", entityId, assetLabel: "trailer" } : null;
  }

  if (action.startsWith("Dispatcher")) {
    const entityId =
      auditLog.links.find(
        (link) => link.entityType === "Employee" && link.role === "Dispatcher"
      )?.entityId ?? readNumber(metadata.dispatcherEmployeeId);

    return entityId ? { entityType: "Employee", entityId, assetLabel: "dispatcher" } : null;
  }

  return null;
};

export const getAssignmentActionPhrase = (action: string, assetLabel: string) => {
  if (isAssignmentUnassignAction(action)) {
    return `Unassigned from ${assetLabel}`;
  }

  return `Assigned ${assetLabel}`;
};

type DisplayAuditLink = Pick<AuditLogLink, "entityType" | "entityId" | "role">;

export const getAssignmentAssetLink = (
  auditLog: AuditLog,
  excludeEntityType?: string,
  excludeEntityId?: number
): DisplayAuditLink | null => {
  const asset = getAssignmentAssetInfo(auditLog);
  if (!asset) {
    return null;
  }

  if (excludeEntityType === asset.entityType && excludeEntityId === asset.entityId) {
    return null;
  }

  return {
    entityType: asset.entityType,
    entityId: asset.entityId,
    role:
      asset.entityType === "Employee"
        ? "Dispatcher"
        : asset.entityType === "Vehicle"
          ? "Vehicle"
          : "Trailer",
  };
};
